import { useState, useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { OnboardingData } from "@/types/onboarding";
import { validatePassword } from "@/lib/validation";
import { ArrowLeft, ArrowRight, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface AdminInfoStepProps {
  data: OnboardingData;
  onChange: (updates: Partial<OnboardingData>) => void;
  onNext: () => void;
  onBack: () => void;
}

interface PasswordRequirement {
  label: string;
  test: (pw: string) => boolean;
}

const requirements: PasswordRequirement[] = [
  { label: "At least 8 characters", test: (pw) => pw.length >= 8 },
  { label: "One uppercase letter", test: (pw) => /[A-Z]/.test(pw) },
  { label: "One lowercase letter", test: (pw) => /[a-z]/.test(pw) },
  { label: "One number", test: (pw) => /[0-9]/.test(pw) },
];

function getStrength(password: string): { level: number; label: string; color: string } {
  if (!password) return { level: 0, label: "", color: "" };
  const passed = requirements.filter((r) => r.test(password)).length;
  if (passed <= 1) return { level: 1, label: "Weak", color: "bg-red-500" };
  if (passed === 2) return { level: 2, label: "Fair", color: "bg-amber-500" };
  if (passed === 3) return { level: 3, label: "Good", color: "bg-primary" };
  return { level: 4, label: "Strong", color: "bg-green-500" };
}

export default function AdminInfoStep({ data, onChange, onNext, onBack }: AdminInfoStepProps) {
  const [showErrors, setShowErrors] = useState(false);

  const strength = useMemo(() => getStrength(data.adminPassword), [data.adminPassword]);
  const passwordsMatch =
    data.confirmPassword.length > 0 && data.adminPassword === data.confirmPassword;
  const passwordsMismatch =
    data.confirmPassword.length > 0 && data.adminPassword !== data.confirmPassword;

  const handleNext = () => {
    const passwordValidation = validatePassword(data.adminPassword);

    if (!passwordValidation.isValid) {
      setShowErrors(true);
      document.getElementById("adminPassword")?.focus();
      return;
    }

    if (data.adminPassword !== data.confirmPassword) {
      setShowErrors(true);
      document.getElementById("confirmPassword")?.focus();
      return;
    }

    onNext();
  };

  return (
    <div className="space-y-6">
      {/* Password field */}
      <div className="space-y-2">
        <Label htmlFor="adminPassword">
          Password <span className="text-red-500">*</span>
        </Label>
        <PasswordInput
          id="adminPassword"
          value={data.adminPassword}
          onChange={(e) => onChange({ adminPassword: e.target.value })}
          placeholder="Create a strong password"
          autoComplete="new-password"
        />

        {/* Strength meter */}
        {data.adminPassword && (
          <div className="space-y-2 pt-1">
            <div className="flex items-center gap-2">
              <div className="flex gap-1 flex-1">
                {[1, 2, 3, 4].map((bar) => (
                  <div
                    key={bar}
                    className={cn(
                      "h-1 flex-1 rounded-full transition-colors duration-200",
                      bar <= strength.level ? strength.color : "bg-slate-200"
                    )}
                  />
                ))}
              </div>
              <span
                className={cn(
                  "text-xs font-medium",
                  strength.level <= 1 && "text-red-600",
                  strength.level === 2 && "text-amber-600",
                  strength.level === 3 && "text-primary",
                  strength.level === 4 && "text-green-600"
                )}
              >
                {strength.label}
              </span>
            </div>

            {/* Requirements checklist */}
            <ul className="space-y-1">
              {requirements.map((req) => {
                const passed = req.test(data.adminPassword);
                return (
                  <li key={req.label} className="flex items-center gap-2 text-xs">
                    {passed ? (
                      <Check className="h-3.5 w-3.5 text-green-600" strokeWidth={2.5} />
                    ) : (
                      <X className="h-3.5 w-3.5 text-slate-300" strokeWidth={2.5} />
                    )}
                    <span className={passed ? "text-slate-600" : "text-slate-400"}>
                      {req.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {/* Confirm password field */}
      <div className="space-y-1.5">
        <Label htmlFor="confirmPassword">
          Confirm password <span className="text-red-500">*</span>
        </Label>
        <PasswordInput
          id="confirmPassword"
          value={data.confirmPassword}
          onChange={(e) => onChange({ confirmPassword: e.target.value })}
          placeholder="Re-enter your password"
          autoComplete="new-password"
          className={
            showErrors && passwordsMismatch ? "border-red-500 focus-visible:ring-red-500/20" : ""
          }
        />
        {passwordsMatch && (
          <p className="text-xs text-green-600 flex items-center gap-1">
            <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
            Passwords match
          </p>
        )}
        {showErrors && passwordsMismatch && (
          <p className="text-xs text-red-600" role="alert">
            Passwords do not match
          </p>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-2">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button onClick={handleNext} size="lg">
          Continue
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
